import React, { useState, useEffect, useCallback } from 'react';
import { 
  ShoppingBag,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import axios from '../../services/api';
import { formatMoney } from '../../lib/utils';

const MyOrders = () => {
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    total: 0,
    pageSize: 20
  });

  const loadOrders = useCallback(async (page = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      // 确保page是数字类型
      const pageNumber = typeof page === 'number' ? page : 0;
      
      // 调用后端订单API
      const response = await axios.get('/orders/user', {
        params: {
          page: pageNumber,
          size: 20 // 使用固定的页面大小，避免循环依赖
        }
      });
      
      if (response.data.success) {
        setOrders(response.data.orders || []);
        setPagination({
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          total: response.data.total,
          pageSize: response.data.pageSize
        });
      } else {
        throw new Error(response.data.error || 'Failed to load orders');
      }
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []); // 移除pagination.pageSize依赖，避免循环依赖

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatStatus = (status) => {
    const statusMap = {
      'PAID': t('orders.statusCompleted') || '已完成',
      'PENDING': t('orders.statusPending') || '待处理',
      'FAILED': t('orders.statusFailed') || '失败',
      'CANCELLED': t('orders.statusCancelled') || '已取消',
      'REFUNDED': t('orders.statusRefunded') || '已退款'
    };
    return statusMap[status] || status;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      loadOrders(newPage);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={loadOrders} className="retry-button">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="my-orders">
      {/* Page Title */}
      <div className="page-header">
        <h1>{t('personalCenter.myOrders')}</h1>
      </div>

      {/* Order List */}
      <div className="table-container">
        <div className="profile-table-header">
          <h3>{t('orders.orderList')}</h3>
          <div className="pagination-controls" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <button 
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 0}
              className="pagination-btn"
              style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: pagination.currentPage === 0 ? '#f5f5f5' : '#fff',
                cursor: pagination.currentPage === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ChevronLeft size={16} />
            </button>
            <span style={{fontSize: '14px', color: '#666'}}>
              {pagination.currentPage + 1} / {pagination.totalPages || 1}
            </span>
            <button 
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= pagination.totalPages - 1}
              className="pagination-btn"
              style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: pagination.currentPage >= pagination.totalPages - 1 ? '#f5f5f5' : '#fff',
                cursor: pagination.currentPage >= pagination.totalPages - 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        
        {orders.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('orders.orderNo')}</th>
                  <th>{t('orders.productName')}</th>
                  <th>{t('orders.amount')}</th>
                  <th>{t('orders.status')}</th>
                  <th>{t('orders.credits')}</th>
                  <th>{t('orders.paidAt')}</th>
                  {/* <th>{t('orders.operations')}</th> */}
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => (
                  <tr key={order.id || index}>
                    <td>{order.orderNumber || '-'}</td>
                    <td>{order.packageName || '-'}</td>
                    <td>{ formatMoney(order.amount || '0.00', order.currency)}</td>
                    <td>
                      <span className={`status-badge status-${order.status?.toLowerCase()}`}>
                        {formatStatus(order.status)}
                      </span>
                    </td>
                    <td>{order.credits || 0}</td>
                    <td>{formatDate(order.completedAt)}</td>
                    {/* <td>
                      {canRefund(order) ? (
                        <button
                          onClick={() => handleRefund(order)}
                          disabled={refundingOrders.has(order.id)}
                          className={`refund-btn ${refundingOrders.has(order.id) ? 'loading' : ''}`}
                        >
                          {refundingOrders.has(order.id) ? (
                            <>
                              <RefreshCw size={14} className="spin-icon" />
                              {t('orders.refunding')}
                            </>
                          ) : (
                            <>
                              <AlertCircle size={14} />
                              {t('orders.refund')}
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="refund-unavailable">
                          {t('orders.refundNotAvailable')}
                        </span>
                      )}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <ShoppingBag size={48} />
            </div>
            <h3>{t('orders.noOrders')}</h3>
            <p>{t('orders.noOrdersDescription')}</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default MyOrders;
